// ============================================================================
// STUDIO CRM SERVICE
// Frontend API client for linking studio bookings to Dynamics 365 accounts
// and opportunities (Tier 1 + Tier 2 facilitators + system admins)
// ============================================================================

import { api } from './api';
import { StudioBooking } from './studio-bookings';
import type { CurrencyTotals, AttributionSplit } from '../components/studio-crm/crm-totals';

// ============================================================================
// TYPES
// ============================================================================

export type OpportunityState = 'open' | 'won' | 'lost';
export type CrmAttribution = 'sourced' | 'influenced';
export type CrmCurrency = 'GBP' | 'EUR' | 'USD' | 'CAD';
export type CrmSyncStatus = 'manual' | 'ok' | 'not_visible';
export type CrmExclusionReason = 'internal' | 'training' | 'demo' | 'partner' | 'other';

export interface OpportunityValues {
  name: string;
  state: OpportunityState;
  stage?: string;
  value: number;
  currency: CrmCurrency;
  closeDate?: string;
}

export interface CurrentOpportunityValues extends OpportunityValues {
  ownerName?: string;
  accountId?: string;
  createdOn?: string;
}

export interface BookingOpportunityLink {
  bookingId: string;
  opportunityId: string;
  attribution: CrmAttribution;
  snapshot: OpportunityValues;
  current: CurrentOpportunityValues | null;
  syncStatus: CrmSyncStatus;
  lastSyncedAt?: string;
  linkedBy: string;
  linkedByName: string;
  linkedAt: string;
  updatedBy: string;
  updatedAt: string;
}

export interface BookingAccountLink {
  bookingId: string;
  accountName: string;
  accountId: string | null;
  updatedBy: string;
  updatedAt: string;
}

// Mirrors the backend whitelist (CRM_BOOKING_FIELDS)
export type CrmBookingSummary = Pick<StudioBooking,
  'bookingId' | 'roomName' | 'customer' | 'sector' | 'eventTypeName' | 'facilitatorName' | 'facilitatorTier'
  | 'billingType' | 'customerStatus' | 'dateType' | 'startDate' | 'endDate' | 'status' | 'organizationId'>;

export interface BookingCrmExclusion {
  bookingId: string;
  reason: CrmExclusionReason;
  note?: string;
  excludedBy: string;
  excludedByName: string;
  excludedAt: string;
}

export interface CrmBooking extends CrmBookingSummary {
  crm: {
    account: BookingAccountLink | null;
    opportunities: BookingOpportunityLink[];
    exclusion: BookingCrmExclusion | null;
    notes: CrmNotesSummary | null;
  };
}

export interface CrmNotesSummary {
  count: number;
  lastNoteAt: string | null;
}

export type CrmActivityType =
  | 'opportunity_linked' | 'opportunity_updated' | 'opportunity_unlinked'
  | 'account_set' | 'account_cleared' | 'excluded' | 'included' | 'email_sent';

export interface CrmNote {
  noteId: string;
  bookingId: string;
  kind: 'note' | 'activity';
  body?: string;
  activity?: { type: CrmActivityType; summary: string };
  opportunityId?: string;
  authorId: string;
  authorName: string;
  createdAt: string;
  editedAt?: string;
}

export const MAX_NOTE_LENGTH = 2000;

// ============================================================================
// CONSTANTS
// ============================================================================

export const DYNAMICS_ORG_URL = process.env.REACT_APP_DYNAMICS_ORG_URL || 'https://version1.crm4.dynamics.com';

export const OPPORTUNITY_STATES: OpportunityState[] = ['open', 'won', 'lost'];
export const CRM_CURRENCIES: CrmCurrency[] = ['GBP', 'EUR', 'USD', 'CAD'];

export const STATE_LABELS: Record<OpportunityState, string> = { open: 'Open', won: 'Won', lost: 'Lost' };
export const STATE_COLORS: Record<OpportunityState, string> = {
  open: 'text-sky-700 bg-sky-50 border-sky-200',
  won:  'text-green-700 bg-green-50 border-green-200',
  lost: 'text-gray-500 bg-gray-50 border-gray-200'
};
export const ATTRIBUTION_LABELS: Record<CrmAttribution, string> = { sourced: 'Sourced', influenced: 'Influenced' };
export const EXCLUSION_REASON_LABELS: Record<CrmExclusionReason, string> = {
  internal: 'Internal', training: 'Training', demo: 'Demo / showcase', partner: 'Partner / vendor', other: 'Other'
};

export function opportunityUrl(opportunityId: string): string {
  return `${DYNAMICS_ORG_URL}/main.aspx?pagetype=entityrecord&etn=opportunity&id=${opportunityId}`;
}

// Mirrors the backend parser: a Dynamics opportunity URL (id=…) or a bare GUID,
// braces optional; URLs for other record types are rejected. Used for the live
// preview; the backend re-validates.
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function parseOpportunityRef(ref: string): string | null {
  const trimmed = ref.trim();
  if (!trimmed) return null;
  let candidate = trimmed;
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const params = new URL(trimmed).searchParams;
      const etn = params.get('etn');
      if (etn && etn.toLowerCase() !== 'opportunity') return null;
      const etc = params.get('etc');
      if (etc && etc !== '3') return null;  // legacy object type code; 3 = opportunity
      candidate = params.get('id') || '';
    } catch {
      return null;
    }
  }
  const bare = candidate.replace(/^\{|\}$/g, '');
  return GUID.test(bare) ? bare.toLowerCase() : null;
}

// Synced values win over hand-entered ones while the sync can see the opp.
// not_visible links fall back to the (editable) snapshot.
export function effectiveValues(link: BookingOpportunityLink): OpportunityValues {
  return link.syncStatus === 'ok' && link.current ? link.current : link.snapshot;
}

// ============================================================================
// API
// ============================================================================

export async function listCrmBookings(): Promise<CrmBooking[]> {
  const res = await api.get<{ bookings: CrmBooking[] }>('/api/studio/crm/bookings');
  return res.data.bookings;
}

export async function setBookingAccount(bookingId: string, accountName: string): Promise<BookingAccountLink> {
  const res = await api.put<{ account: BookingAccountLink }>(`/api/studio/crm/bookings/${bookingId}/account`, { accountName });
  return res.data.account;
}

export async function clearBookingAccount(bookingId: string): Promise<void> {
  await api.delete(`/api/studio/crm/bookings/${bookingId}/account`);
}

export async function linkOpportunity(
  bookingId: string,
  input: { opportunityRef: string; attribution: CrmAttribution; snapshot: OpportunityValues }
): Promise<BookingOpportunityLink> {
  const res = await api.post<{ opportunity: BookingOpportunityLink }>(`/api/studio/crm/bookings/${bookingId}/opportunities`, input);
  return res.data.opportunity;
}

export async function updateOpportunity(
  bookingId: string,
  opportunityId: string,
  input: { attribution?: CrmAttribution; snapshot?: OpportunityValues }
): Promise<BookingOpportunityLink> {
  const res = await api.patch<{ opportunity: BookingOpportunityLink }>(
    `/api/studio/crm/bookings/${bookingId}/opportunities/${opportunityId}`, input
  );
  return res.data.opportunity;
}

export interface CrmFxRates {
  base: 'EUR';
  rates: Partial<Record<Exclude<CrmCurrency, 'EUR'>, number>>;  // EUR per 1 unit
  updatedBy: string;
  updatedByName: string;
  updatedAt: string;
}

export async function getFxRates(): Promise<CrmFxRates | null> {
  const res = await api.get<{ fxRates: CrmFxRates | null }>('/api/studio/crm/fx-rates');
  return res.data.fxRates;
}

export interface CrmImpact {
  coverage: { linked: number; past: number };
  opportunityCount: number;
  openCount: number;
  wonCount: number;
  lostCount: number;
  winRate: number | null;
  openPipeline: CurrencyTotals;
  wonTcv: CurrencyTotals;
  pipelineSplit: AttributionSplit;
  wonSplit: AttributionSplit;
  fxRates: Pick<CrmFxRates, 'rates' | 'updatedAt' | 'updatedByName'> | null;
}

// All-time figures over every booking, no booking rows or customer names
export async function getCrmImpact(): Promise<CrmImpact> {
  const res = await api.get<CrmImpact>('/api/studio/crm/impact');
  return res.data;
}

export async function setFxRates(rates: CrmFxRates['rates']): Promise<CrmFxRates> {
  const res = await api.put<{ fxRates: CrmFxRates }>('/api/studio/crm/fx-rates', { rates });
  return res.data.fxRates;
}

export async function listNotes(bookingId: string): Promise<CrmNote[]> {
  const res = await api.get<{ notes: CrmNote[] }>(`/api/studio/crm/bookings/${bookingId}/notes`);
  return res.data.notes;
}

export async function addNote(bookingId: string, input: { body: string; opportunityId?: string }): Promise<CrmNote> {
  const res = await api.post<{ note: CrmNote }>(`/api/studio/crm/bookings/${bookingId}/notes`, input);
  return res.data.note;
}

export async function editNote(bookingId: string, noteId: string, body: string): Promise<CrmNote> {
  const res = await api.patch<{ note: CrmNote }>(`/api/studio/crm/bookings/${bookingId}/notes/${noteId}`, { body });
  return res.data.note;
}

export async function deleteNote(bookingId: string, noteId: string): Promise<void> {
  await api.delete(`/api/studio/crm/bookings/${bookingId}/notes/${noteId}`);
}

export async function excludeBooking(
  bookingId: string,
  input: { reason: CrmExclusionReason; note?: string }
): Promise<BookingCrmExclusion> {
  const res = await api.put<{ exclusion: BookingCrmExclusion }>(`/api/studio/crm/bookings/${bookingId}/exclusion`, input);
  return res.data.exclusion;
}

export async function includeBooking(bookingId: string): Promise<void> {
  await api.delete(`/api/studio/crm/bookings/${bookingId}/exclusion`);
}

export async function unlinkOpportunity(bookingId: string, opportunityId: string): Promise<void> {
  await api.delete(`/api/studio/crm/bookings/${bookingId}/opportunities/${opportunityId}`);
}
